import { Card, CardHeader } from '@/components/common';
import { useAuthStore } from '@/store';
import { AgentContactBlock, BrokerContactBlock } from '@/pages/settings/AdvisorCards';
import type { Policy } from '@/types';

/**
 * U-01 — on a customer's policy detail, who to ring about THIS policy.
 *
 * Reads the policy's own `advisors` (sent only to a customer, only on their
 * own policies -- apps/policies/serializers.py `customer_policy_advisors`),
 * not `/customers/me/`: a customer with policies at two brokers, or sold by
 * two agents, must see the right one on each. If the field is missing (an
 * older server), it falls back to the name-only `broker_name` / `posp_name`
 * every policy payload carries rather than claiming nobody is assigned.
 *
 * Customers only: staff and agents have the broker and agent elsewhere on the
 * page, and are never sent `advisors`.
 */
export function PolicyAdvisorCard({ policy }: { policy: Policy }) {
  const role = useAuthStore((state) => state.user?.role);
  if (role !== 'customer') return null;

  const advisors = policy.advisors;
  const broker = advisors ? advisors.broker : { name: policy.broker_name };
  const agent = advisors ? advisors.agent : policy.posp_name ? { name: policy.posp_name } : null;

  return (
    <Card>
      <CardHeader title="Your advisors" subtitle="Who to contact about this policy" />
      <div className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Broker</p>
          <BrokerContactBlock broker={broker} />
        </div>
        <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Agent / POSP</p>
          <AgentContactBlock agent={agent} />
        </div>
      </div>
    </Card>
  );
}
